import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router";
import { useVerifyEmail } from "@platform/hooks";
import { ApiError } from "@platform/api-client";
import { Button } from "@nthamizh/ui";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const verifyEmail = useVerifyEmail();
  const [status, setStatus] = useState<"pending" | "success" | "error">("pending");
  const [errorMessage, setErrorMessage] = useState("");
  // StrictMode double-invokes effects in dev — a token is single-use, so
  // a naive fire-on-mount would burn it on the first (throwaway) pass
  // and always fail on the second, real one.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current || !token) return;
    attempted.current = true;
    verifyEmail.mutateAsync(token)
      .then(() => setStatus("success"))
      .catch((err) => {
        setErrorMessage(err instanceof ApiError ? err.message : "This link is invalid or has expired.");
        setStatus("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // No token at all is a static condition derivable straight from the
  // URL, not something needing an effect + setState — only the actual
  // async verification call above legitimately needs one.
  const effectiveStatus = !token ? "error" : status;
  const effectiveMessage = !token ? "This link is missing its token." : errorMessage;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-[380px] text-center">
        <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-8">
          {effectiveStatus === "pending" && (
            <>
              <Loader2 className="mx-auto mb-4 h-8 w-8 text-blue-600 animate-spin" />
              <h1 className="text-lg font-semibold text-gray-900">Verifying your email…</h1>
            </>
          )}
          {effectiveStatus === "success" && (
            <>
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="text-lg font-semibold text-gray-900">Email verified</h1>
              <p className="mt-2 text-sm text-gray-500">Your account is ready. You can sign in now.</p>
              <Link to="/login">
                <Button className="mt-6">Sign in</Button>
              </Link>
            </>
          )}
          {effectiveStatus === "error" && (
            <>
              <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-50 flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h1 className="text-lg font-semibold text-gray-900">Couldn&apos;t verify</h1>
              <p className="mt-2 text-sm text-gray-500">{effectiveMessage}</p>
              <p className="mt-4 text-sm text-gray-500">
                <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
                  Back to sign in
                </Link>
                {" "}— you can request a new verification email from there.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
